import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OperatingSystem, PlatformService } from '@y42/platform';
import { ensureArray } from '@y42/util/ensure-array';
import isEqual from 'lodash/isEqual';
import Mousetrap, { ExtendedKeyboardEvent, MousetrapInstance, MousetrapStatic } from 'mousetrap';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export enum KeyboardShortcutsNamespace {
  Global = 'Global',
  DataModeling = 'DataModeling',
  DataModelingNodeTransformation = 'DataModelingNodeTransformation',
  Visualization = 'Visualization',
}

interface KeySequenceStored {
  sequence: string;
  callback: (e: ExtendedKeyboardEvent, combo: string) => boolean | void | Promise<boolean | void>;
  namespace: KeyboardShortcutsNamespace;
  description?: string;
}

export interface KeySequence extends Omit<Omit<KeySequenceStored, 'sequence'>, 'namespace'> {
  sequence: string | string[];
  namespace?: KeyboardShortcutsNamespace;
}

export interface KeySequenceLabel {
  base: string;
  decorator?: string;
}

export interface KeySequenceLabels extends Omit<KeySequenceStored, 'callback'> {
  labels: KeySequenceLabel[];
  rawLabel: string;
}

interface KeySequenceMap {
  [key: string]: KeySequenceLabel;
}

const COMMON_KEYS_MAP: KeySequenceMap = {
  left: { base: '', decorator: '⇦' },
  right: { base: '', decorator: '⇨' },
  shift: { base: 'shift', decorator: '⇧' },
  tab: { base: 'tab', decorator: '↹' },
  '=': { base: '+' },
  enter: { base: '', decorator: '⏎' },
};

const osToKeySequences: Partial<Record<OperatingSystem, KeySequenceMap>> = {
  [OperatingSystem.MacOS]: {
    ...COMMON_KEYS_MAP,
    meta: { base: 'cmd', decorator: '⌘' },
    command: { base: 'cmd', decorator: '⌘' },
    ctrl: { base: 'control' },
    alt: { base: 'option', decorator: '⌥' },
  },
  [OperatingSystem.Linux]: {
    ...COMMON_KEYS_MAP,
    meta: { base: 'meta' },
    command: { base: 'ctrl' },
    ctrl: { base: 'ctrl' },
    alt: { base: 'alt' },
  },
  [OperatingSystem.Windows]: {
    ...COMMON_KEYS_MAP,
    meta: { base: 'start', decorator: '⊞' },
    command: { base: 'ctrl' },
    ctrl: { base: 'ctrl' },
    alt: { base: 'alt', decorator: '⎇' },
  },
  [OperatingSystem.Other]: COMMON_KEYS_MAP,
};

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class KeyboardShortcutsService {
  constructor(private readonly platform: PlatformService) {
    this.initLabelsListener();
    this.overrideStopCallback();
  }

  private readonly mousetrapInstance = new Mousetrap();

  private readonly keySequenceMap: { [key: string]: KeySequenceStored } = {};
  private readonly update$ = new Subject<void>();
  private readonly keysMap = osToKeySequences[this.platform.operatingSystem];

  private readonly keySequenceLabelsSubject$ = new BehaviorSubject<KeySequenceLabels[]>([]);
  public readonly keySequenceLabels$ = this.keySequenceLabelsSubject$.pipe(untilDestroyed(this));

  /**
   * https://craig.is/killing/mice#api.stopCallback
   */
  private readonly defaultMousetrapStopCallback: MousetrapInstance['stopCallback'] = (
    e: ExtendedKeyboardEvent,
    element: Element,
  ) => {
    // if the element has the class "mousetrap" then no need to stop
    if (element.classList.contains('mousetrap')) {
      return false;
    }

    // stop for editable elements
    return (
      element.tagName === 'INPUT' ||
      element.tagName === 'SELECT' ||
      element.tagName === 'TEXTAREA' ||
      (!!(element as HTMLElement).contentEditable && (element as HTMLElement).contentEditable === 'true')
    );
  };

  public register(keySequence: KeySequence) {
    const { callback, description } = keySequence;
    let namespace = keySequence.namespace;
    if (!namespace) {
      namespace = KeyboardShortcutsNamespace.Global;
    }
    if (Array.isArray(keySequence.sequence)) {
      keySequence.sequence.forEach((sequence) =>
        this.registerSingle(sequence, callback, namespace as KeyboardShortcutsNamespace, description),
      );
    } else {
      this.registerSingle(keySequence.sequence, callback, namespace, description);
    }
  }

  public unregister(sequence: KeySequence['sequence']) {
    if (Array.isArray(sequence)) {
      sequence.forEach((seq) => this.unregisterSingle(seq));
    } else {
      this.unregisterSingle(sequence);
    }
  }

  public getLabels(sequence: string): KeySequenceLabel[] {
    return sequence.split('+').map((key) => this.getPlatformSpecificButton(key));
  }

  public getRawLabel(labels: KeySequenceLabel[]) {
    return labels.map((l) => l.base).join(' + ');
  }

  public getHtmlLabelsForSequence(keySequence: KeySequence['sequence']): string[] {
    return (
      ensureArray(keySequence)
        .map((sequence) => this.getLabels(sequence))
        // remove duplicates
        .reduce<KeySequenceLabel[][]>((acc, curr) => {
          const alreadyExists = acc.find((label) => isEqual(label, curr));
          if (alreadyExists) {
            return acc;
          }
          return [...acc, curr];
        }, [])
        .map((labels) =>
          labels
            .map(
              (label) =>
                `<kbd class="shortcut-key">
                  <span class="${label.base === 'cmd' ? 'cmd-key' : ''}">${(
                  label.decorator ?? label.base
                ).toUpperCase()}</span>
                </kbd>`,
            )
            .join(''),
        )
    );
  }

  public getTooltipFormatter(sequence: string, prefixDescription: string) {
    return this.keySequenceLabels$.pipe(
      map((keySequenceLabels) => {
        const foundKeySequence = keySequenceLabels.find((label) => label.sequence === sequence);
        return foundKeySequence
          ? `${prefixDescription} (${foundKeySequence.rawLabel.toUpperCase()})`
          : prefixDescription;
      }),
      untilDestroyed(this),
    );
  }

  private registerSingle(
    sequence: string,
    callback: (e: ExtendedKeyboardEvent, combo: string) => boolean | void | Promise<boolean | void>,
    namespace: KeyboardShortcutsNamespace,
    description: KeySequence['description'],
  ) {
    if (this.keySequenceMap[sequence]) {
      console.error('You want to register already registered sequence', sequence);
      // TODO: errorHandler.handleError call
    } else {
      this.keySequenceMap[sequence] = { sequence, callback, description, namespace };
      this.mousetrapInstance.bind(sequence, (e, combo) => {
        callback(e, combo);
        return true;
      });
      this.update$.next();
    }
  }

  private unregisterSingle(sequence: string) {
    delete this.keySequenceMap[sequence];
    this.mousetrapInstance.unbind(sequence);
    this.update$.next();
  }

  private initLabelsListener() {
    this.update$
      .pipe(
        map(() =>
          Object.keys(this.keySequenceMap)
            .map((key): KeySequenceLabels | undefined => {
              const keySequence = this.keySequenceMap[key];
              if (!keySequence) {
                return undefined;
              }
              const { namespace, description } = keySequence;

              const labels = this.getLabels(key);
              const rawLabel = this.getRawLabel(labels);
              return {
                sequence: key,
                namespace,
                description,
                labels,
                rawLabel,
              };
            })
            .filter((label: KeySequenceLabels | undefined): label is KeySequenceLabels => !!label),
        ),
        tap((labels) => this.keySequenceLabelsSubject$.next(labels)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private getPlatformSpecificButton(sequenceChunk: string): KeySequenceLabel {
    const foundInMap = this.keysMap?.[sequenceChunk];

    if (foundInMap) {
      return { ...foundInMap };
    }

    return {
      base: sequenceChunk,
    };
  }

  private overrideStopCallback() {
    const stopCallbackOverride: MousetrapStatic['stopCallback'] = (
      e: ExtendedKeyboardEvent,
      element: Element,
      combo: string,
    ) => {
      // The CMDK is the only input where we want to make exceptions and allow keyboard shortcuts
      // Within the CMDK input, we want to allow shortcuts that involve more than one character
      // This ensures all regular keyup are going to be ignored
      if (element.classList.contains('cmdk-input')) {
        const isSingleCharPattern = /^.$/;
        const isSingleCharCombo = combo.length === 1 && isSingleCharPattern.test(combo[0] ?? '');
        return isSingleCharCombo;
      }

      return this.defaultMousetrapStopCallback(e, element, combo);
    };

    this.mousetrapInstance.stopCallback = stopCallbackOverride;
  }
}
